package com.swetdot.swetgram.ui.picso

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.grid.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import com.swetdot.swetgram.domain.*
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.fabStackOverFloatingNav
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.*
import kotlin.time.Instant

@Composable
fun PicsoScreen(
    state: PicsoUiState,
    me: UserId,
    onSwitchTab: (PicsoFeedTab) -> Unit,
    onRefresh: () -> Unit,
    onLoadMore: () -> Unit,
    onSelectPost: (PicsoPostItem?) -> Unit,
    onReact: (PicsoId, String) -> Unit,
    onToggleSave: (PicsoId) -> Unit,
    onDeletePost: (PicsoId) -> Unit,
    onOpenCapture: () -> Unit,
    onOpenProfile: (String) -> Unit,
    onReportPost: (PicsoId) -> Unit,
    onShare: (PicsoPostItem) -> Unit,
    onShareTo: (ChatId) -> Unit,
    onDismissShare: () -> Unit,
    loadImage: suspend (String) -> ByteArray,
    now: () -> Instant,
    navClearance: Dp = 0.dp,
    modifier: Modifier = Modifier,
) {
    LifecycleEventEffect(Lifecycle.Event.ON_RESUME) { onRefresh() }
    val gridState = rememberLazyGridState()
    val fab = fabStackOverFloatingNav(56.dp, navClearance)
    Box(modifier.fillMaxSize().background(Color.Black)) {
        Column(Modifier.fillMaxSize()) {
            Row(Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("Picso", color = Color.White, fontSize = 32.sp, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                IconButton(onClick = { onSwitchTab(if (state.feedTab == PicsoFeedTab.SAVED) PicsoFeedTab.ALL else PicsoFeedTab.SAVED) }) {
                    SwetIcon(if (state.feedTab == PicsoFeedTab.SAVED) SwetIcons.BookmarkFilled else SwetIcons.Bookmark, contentDescription = stringResource(Res.string.picso_tab_saved), tint = Color.White)
                }
                TextButton(onClick = onRefresh, enabled = !state.isRefreshing && !state.isLoading) { Text(stringResource(Res.string.picso_refresh)) }
            }
            if (state.feedTab == PicsoFeedTab.SAVED) Text(stringResource(Res.string.picso_tab_saved), color = Color.White, modifier = Modifier.padding(horizontal = 16.dp))
            if (state.isRefreshing) LinearProgressIndicator(Modifier.fillMaxWidth())
            if (state.error || state.actionError) {
                TextButton(onClick = onRefresh) { Text(stringResource(Res.string.picso_error_load) + " · " + stringResource(Res.string.picso_retry)) }
            }
            if (state.isLoading) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
            } else if (state.items.isEmpty()) {
                Column(Modifier.fillMaxSize().padding(28.dp), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                    Text(stringResource(Res.string.picso_empty_feed), color = Color.White, fontSize = 22.sp)
                    Text(stringResource(Res.string.picso_empty_feed_sub), color = Color.LightGray, modifier = Modifier.padding(vertical = 12.dp))
                    Button(onClick = onOpenCapture) { Text(stringResource(Res.string.picso_button_snap)) }
                }
            } else {
                LazyVerticalGrid(
                    columns = GridCells.Fixed(2), state = gridState,
                    contentPadding = PaddingValues(start = 12.dp, end = 12.dp, top = 8.dp, bottom = fab.listReservation),
                    horizontalArrangement = Arrangement.spacedBy(10.dp), verticalArrangement = Arrangement.spacedBy(10.dp),
                    modifier = Modifier.fillMaxSize(),
                ) {
                    items(state.items, key = { it.id.value }) { post -> PicsoCard(post, { onSelectPost(post) }, loadImage, now) }
                    if (state.hasMore) item(span = { GridItemSpan(2) }) {
                        TextButton(onClick = onLoadMore, enabled = !state.isLoadingMore) {
                            if (state.isLoadingMore) CircularProgressIndicator(Modifier.size(20.dp)) else Text(stringResource(Res.string.picso_load_more))
                        }
                    }
                }
            }
        }
        FloatingActionButton(onClick = onOpenCapture, containerColor = Color.White, contentColor = Color.Black,
            modifier = Modifier.align(Alignment.BottomEnd).padding(end = 20.dp, bottom = 20.dp + fab.discLift)) {
            SwetIcon(SwetIcons.CreateDiscoverPost, contentDescription = stringResource(Res.string.picso_button_snap), tint = Color.Black)
        }
        if (state.isOpening) CircularProgressIndicator(Modifier.align(Alignment.Center))
    }
    state.selectedPost?.let { selected ->
        PicsoDetailViewer(
            post = selected, now = now, loadImage = loadImage, busy = selected.id in state.busyIds,
            onDismiss = { onSelectPost(null) }, onReact = { onReact(selected.id, it) },
            onToggleSave = { onToggleSave(selected.id) }, onDelete = { onDeletePost(selected.id) },
            onOpenProfile = { onOpenProfile(selected.authorUsername) }, onShare = { onShare(selected) },
            onReport = { onSelectPost(null); onReportPost(selected.id) },
        )
    }
    PicsoSharePicker(state, me, onShareTo, onDismissShare)
}

@Composable
fun PicsoSharePicker(state: PicsoUiState, me: UserId, onShareTo: (ChatId) -> Unit, onDismissShare: () -> Unit) {
    if (state.sharePost != null) {
        AlertDialog(
            onDismissRequest = onDismissShare,
            title = { Text(stringResource(Res.string.picso_share)) },
            text = {
                Column {
                    if (state.actionError) Text(stringResource(Res.string.picso_error_load))
                    if (state.isLoadingChats || state.isSharing) CircularProgressIndicator()
                    if (!state.isLoadingChats && state.shareChats.isEmpty()) Text(stringResource(Res.string.picso_no_chats))
                    LazyColumn(Modifier.heightIn(max = 360.dp)) {
                        items(state.shareChats, key = { it.id.value }) { chat ->
                            TextButton(onClick = { onShareTo(chat.id) }, enabled = !state.isSharing) { Text(chat.displayTitle(me)) }
                        }
                    }
                }
            },
            confirmButton = { TextButton(onClick = onDismissShare, enabled = !state.isSharing) { Text(stringResource(Res.string.picso_cancel)) } },
        )
    }
}
